import React, { useState } from 'react';
import { ExclamationTriangleIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import {
  PERSONALITY_TRAITS,
  LIKERT_LABELS,
  QUALITY_FLAG_LABELS,
  PERSONALITY_CREDIT,
  isPersonalityResult,
  traitScore,
} from './personalityProfile';

const MIN = 10;
const MAX = 50;

/**
 * Recruiter view of a personality (IPIP-50) result: one bar per trait showing where the raw
 * score sits on the full 10-50 range, the response-quality flag, and the statement answers
 * (neutral — there are no correct answers).
 *
 * @param {Object} props
 * @param {Object|null} props.result - personalityResult from the attempt or module
 * @param {Array} [props.answers] - result rows ({ question, userAnswer }) from the results API
 */
function PersonalityProfilePanel({ result, answers = [] }) {
  const [showAnswers, setShowAnswers] = useState(false);

  if (!isPersonalityResult(result)) {
    return (
      <div className="px-6 py-10 text-center text-sm text-gray-500" data-testid="personality-profile-empty">
        No work style profile yet — the candidate has not finished the questionnaire.
      </div>
    );
  }

  const quality = result.quality || {};
  const flags = (quality.flags || []).filter((f) => QUALITY_FLAG_LABELS[f]);

  return (
    <div className="p-5 space-y-5" data-testid="personality-profile">
      <div>
        <h4 className="text-lg font-semibold text-gray-900">Work Style Profile</h4>
        <p className="text-xs text-gray-500 mt-0.5">
          Each bar shows the candidate&apos;s score on the full {MIN}–{MAX} range. Scores describe tendencies, not
          ability, and there is no right profile.
        </p>
      </div>

      {quality.lowConfidence && (
        <div className="flex gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="personality-low-confidence">
          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
          <div>
            <p className="font-medium">Low confidence — read this profile with care.</p>
            <ul className="list-disc pl-4 text-xs mt-0.5">
              {flags.map((f) => (
                <li key={f}>{QUALITY_FLAG_LABELS[f]}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {PERSONALITY_TRAITS.map((t) => {
          const raw = traitScore(result, t.key);
          const trait = (result.traits || []).find((x) => x.trait === t.key) || {};
          const pct = raw === null ? 0 : ((raw - MIN) / (MAX - MIN)) * 100;
          return (
            <div key={t.key} data-testid={`personality-trait-${t.key}`}>
              <div className="flex items-baseline justify-between text-sm">
                <span className="font-medium text-gray-900">{t.label}</span>
                <span className="tabular-nums text-gray-700">
                  {raw === null ? 'Incomplete' : `${Math.round(raw)} / ${MAX}`}
                  {trait.status === 'prorated' && (
                    <span className="ml-1 text-[11px] text-gray-400" title="Estimated from the statements answered">
                      (est.)
                    </span>
                  )}
                </span>
              </div>
              <div className="relative mt-1.5 h-2.5 rounded-full bg-gray-100">
                {raw !== null && (
                  <>
                    <div className="absolute inset-y-0 left-0 rounded-full bg-indigo-200" style={{ width: `${pct}%` }} />
                    <div
                      className="absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-indigo-600 shadow"
                      style={{ left: `${pct}%` }}
                    />
                  </>
                )}
              </div>
              <div className="mt-1 flex justify-between text-[11px] text-gray-500">
                <span>{t.low}</span>
                <span>{t.high}</span>
              </div>
            </div>
          );
        })}
      </div>

      {answers.length > 0 && (
        <div className="rounded-lg border border-gray-200">
          <button
            type="button"
            onClick={() => setShowAnswers((v) => !v)}
            className="flex w-full items-center justify-between px-3 py-2 text-sm font-medium text-gray-700"
          >
            Statement answers ({answers.length})
            <ChevronDownIcon className={`h-4 w-4 transition-transform ${showAnswers ? 'rotate-180' : ''}`} />
          </button>
          {showAnswers && (
            <ol className="divide-y divide-gray-100 border-t border-gray-200 text-sm">
              {answers.map((row, idx) => (
                <li key={row.questionId || idx} className="flex justify-between gap-3 px-3 py-1.5">
                  <span className="text-gray-800">
                    <span className="text-gray-400 mr-1.5">{idx + 1}.</span>
                    {row.question?.content || row.question?.title}
                  </span>
                  <span className="whitespace-nowrap text-gray-600">{LIKERT_LABELS[Number(row.userAnswer)] || 'No answer'}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}

      <p className="text-[11px] text-gray-400">{PERSONALITY_CREDIT}</p>
    </div>
  );
}

export default PersonalityProfilePanel;
